/**
 * Staff-notification bell (PF-135).
 *
 * GET /notifications + /notifications/unread-count, mark-read mutations, and
 * cache invalidation on socket `notification:new`.
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { api } from '@/lib/api';
import { formatDateTime } from '@/utils/locale';
import { formatCentsForDisplay } from '@/utils/money';
import { sveaPaymentMethodKind, sveaPaymentMethodLabelKey } from '@/utils/sveaPaymentMethod';
import { queryKeys } from './queryKeys';
import { useWebSocket } from './useWebSocket';

export interface StaffNotification {
  id: string;
  type: string;
  titleKey: string;
  bodyKey: string | null;
  params: Record<string, unknown>;
  deepLink: string | null;
  entityType: string | null;
  entityId: string | null;
  visibleFrom: string;
  createdAt: string;
  expiresAt: string | null;
  readAt: string | null;
  dismissedAt: string | null;
}

interface NotificationListResponse {
  notifications: StaffNotification[];
}

interface UnreadCountResponse {
  count: number;
}

/**
 * Backend title/body keys are `notifications.vehicleDropoff.title`, but
 * `common.notifications` is already the bell label string. Map into
 * `staffNotifications.*` so i18next can nest the item copy.
 */
export function staffNotificationMessageKey(backendKey: string): string {
  return backendKey.startsWith('notifications.')
    ? `staffNotifications.${backendKey.slice('notifications.'.length)}`
    : backendKey;
}

/** `common` translator. Callers that already have `tc` can pass it; otherwise the shared i18next instance is used. */
export type StaffNotificationTranslate = (key: string) => string;

function translateCommon(key: string): string {
  return i18next.t(key, { ns: 'common', defaultValue: key });
}

export function staffNotificationParams(
  params: Record<string, unknown>,
  translate: StaffNotificationTranslate = translateCommon,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') {
      out[key] = '—';
      continue;
    }
    if ((key === 'reportedAt' || key === 'installedAt') && typeof value === 'string') {
      out[key] = formatDateTime(value);
      continue;
    }
    // Money travels as integer cents and is shown as `amount` (#3947).
    if (key === 'amountCents' && typeof value === 'number') {
      out['amount'] = formatCentsForDisplay(value);
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = String(value);
    }
  }
  // Svea payment titles name the method (#3961). Rows recorded before the
  // code was stored have no paymentMethodCode and stay on the generic label.
  if ('amountCents' in params || 'paymentMethodCode' in params) {
    const rawCode = params['paymentMethodCode'];
    const kind = sveaPaymentMethodKind(typeof rawCode === 'string' ? rawCode : null);
    out['method'] = translate(kind ? sveaPaymentMethodLabelKey(kind) : 'paymentMethod.online');
  }
  return out;
}

function invalidateStaffNotifications(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
}

export function useStaffNotifications() {
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: () => api.get<NotificationListResponse>('/notifications'),
    staleTime: 15 * 1000,
  });

  const unreadQuery = useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => api.get<UnreadCountResponse>('/notifications/unread-count'),
    staleTime: 15 * 1000,
  });

  const onNotificationNew = useCallback(() => {
    invalidateStaffNotifications(queryClient);
  }, [queryClient]);

  useWebSocket('notification:new', onNotificationNew);

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => invalidateStaffNotifications(queryClient),
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => invalidateStaffNotifications(queryClient),
  });

  return {
    notifications: listQuery.data?.notifications ?? [],
    unreadCount: unreadQuery.data?.count ?? 0,
    isLoading: listQuery.isLoading || unreadQuery.isLoading,
    markAsRead: markRead.mutate,
    markAllAsRead: markAllRead.mutate,
    markAsReadAsync: markRead.mutateAsync,
    markAllAsReadAsync: markAllRead.mutateAsync,
  };
}
